import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import AirQualityDisplay from '@/components/ui/AirQualityDisplay';
import { fetchAirQualityByCity, fetchAirQualityByCoordinates, fetchRegionAirQuality, ProcessedAirQualityData } from '@/services/airQualityService';

const AirQualityTester: React.FC = () => {
  const [cityName, setCityName] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [regionId, setRegionId] = useState('');
  const [airQualityData, setAirQualityData] = useState<ProcessedAirQualityData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastQuery, setLastQuery] = useState('');

  const testByCity = async () => {
    if (!cityName.trim()) return;
    
    setLoading(true);
    setError(null);
    setLastQuery(`City: ${cityName}`);
    
    try {
      const data = await fetchAirQualityByCity(cityName);
      setAirQualityData(data);
      if (!data) {
        setError('No data found for this city');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const testByCoordinates = async () => {
    const lat = parseFloat(latitude);
    const lon = parseFloat(longitude);
    
    if (isNaN(lat) || isNaN(lon)) {
      setError('Please enter valid coordinates');
      return;
    }
    
    setLoading(true);
    setError(null);
    setLastQuery(`Coordinates: ${lat}, ${lon}`);
    
    try {
      const data = await fetchAirQualityByCoordinates(lat, lon);
      setAirQualityData(data);
      if (!data) {
        setError('No monitoring station found near these coordinates');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const testByRegion = async () => {
    if (!regionId.trim()) return;
    
    setLoading(true);
    setError(null);
    setLastQuery(`Region ID: ${regionId}`);
    
    try {
      const data = await fetchRegionAirQuality(regionId);
      setAirQualityData(data);
      if (!data) {
        setError('No air quality data available for this region');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const quickTests = [
    { name: 'Beijing', action: () => { setCityName('Beijing'); setTimeout(() => testByCity(), 100); } },
    { name: 'London', action: () => { setCityName('London'); setTimeout(() => testByCity(), 100); } },
    { name: 'New York', action: () => { setCityName('New York'); setTimeout(() => testByCity(), 100); } },
    { name: 'Beijing Coords', action: () => { setLatitude('39.9042'); setLongitude('116.4074'); setTimeout(() => testByCoordinates(), 100); } }
  ];

  return (
    <div className="space-y-6 p-6 max-w-4xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>🌬️ Air Quality API Tester</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Quick Test Buttons */}
          <div className="space-y-2">
            <h3 className="font-semibold">Quick Tests:</h3>
            <div className="flex flex-wrap gap-2">
              {quickTests.map((test) => (
                <Button
                  key={test.name}
                  variant="outline"
                  size="sm"
                  onClick={test.action}
                  disabled={loading}
                >
                  {test.name}
                </Button>
              ))}
            </div>
          </div>

          {/* City Test */}
          <div className="space-y-2">
            <h3 className="font-semibold">Test by City Name:</h3>
            <div className="flex gap-2">
              <Input
                placeholder="Enter city name (e.g., Beijing, London)"
                value={cityName}
                onChange={(e) => setCityName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && testByCity()}
              />
              <Button onClick={testByCity} disabled={loading || !cityName.trim()}>
                Test City
              </Button>
            </div>
          </div>

          {/* Coordinates Test */}
          <div className="space-y-2">
            <h3 className="font-semibold">Test by Coordinates:</h3>
            <div className="flex gap-2">
              <Input
                placeholder="Latitude (e.g., 39.9042)"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
              />
              <Input
                placeholder="Longitude (e.g., 116.4074)"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
              />
              <Button onClick={testByCoordinates} disabled={loading}>
                Test Coords
              </Button>
            </div>
          </div>

          {/* Region ID Test */}
          <div className="space-y-2">
            <h3 className="font-semibold">Test by Region ID:</h3>
            <div className="flex gap-2">
              <Input
                placeholder="Enter region ID from your database"
                value={regionId}
                onChange={(e) => setRegionId(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && testByRegion()}
              />
              <Button onClick={testByRegion} disabled={loading || !regionId.trim()}>
                Test Region
              </Button>
            </div>
          </div>

          {lastQuery && (
            <div className="text-sm text-muted-foreground">
              Last query: {lastQuery}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      <AirQualityDisplay 
        data={airQualityData}
        loading={loading}
        error={error}
      />

      {/* Debug Info */}
      {airQualityData && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Debug Info</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-xs overflow-auto bg-muted p-2 rounded">
              {JSON.stringify(airQualityData, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AirQualityTester;